import * as THREE from 'three';
import type { CameraConfig } from '../../cameraConfig';

type PrincipalPointShift = { px: number; py: number };

const DEFAULT_SHIFT: PrincipalPointShift = { px: 0, py: 0 };
const PRINCIPAL_POINT_ANIMATION_DURATION = 0.35;

// cubic-bezier(0.4, 0, 0.2, 1) — must match SceneCanvas.module.css canvas bottom transition
const EASING_X1 = 0.4;
const EASING_X2 = 0.2;
const EASING_Y2 = 1;

export const cubicBezierEasing = (x: number): number => {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  // Newton-Raphson: solve parametric t for given x on the bezier x-axis
  let t = x;
  for (let i = 0; i < 8; i++) {
    const t2 = t * t;
    const t3 = t2 * t;
    const mt = 1 - t;
    const mt2 = mt * mt;
    const curX = 3 * mt2 * t * EASING_X1 + 3 * mt * t2 * EASING_X2 + t3;
    const diff = curX - x;
    if (Math.abs(diff) < 1e-7) break;
    const dx = 3 * mt2 * EASING_X1 + 6 * mt * t * (EASING_X2 - EASING_X1) + 3 * t2 * (1 - EASING_X2);
    if (Math.abs(dx) < 1e-7) break;
    t -= diff / dx;
  }
  // Evaluate y-axis: y1=0 term vanishes, so 3*(1-t)*t²*y2 + t³
  const mt = 1 - t;
  return 3 * mt * t * t * EASING_Y2 + t * t * t;
};

export class PrincipalPointController {
  private principalPointMatrix = new THREE.Matrix4();
  private principalPointShift: PrincipalPointShift = { ...DEFAULT_SHIFT };
  private principalPointShiftStart: PrincipalPointShift = { ...DEFAULT_SHIFT };
  private principalPointShiftTarget: PrincipalPointShift = { ...DEFAULT_SHIFT };
  private principalPointAnimationStartTime = 0;
  private principalPointAnimationDuration = 0;
  private isPrincipalPointAnimationActive = false;
  private canvasWidth = 1;
  private canvasHeight = 1;
  private lastViewOffset: CameraConfig['viewOffset'] | null = null;

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly clock: THREE.Clock
  ) {
    const baseUpdateProjectionMatrix = camera.updateProjectionMatrix.bind(camera);
    camera.updateProjectionMatrix = () => {
      baseUpdateProjectionMatrix();
      this.applyPrincipalPointShift();
    };
  }

  setCanvasSize(width: number, height: number): void {
    const newWidth = Math.max(1, width);
    const newHeight = Math.max(1, height);
    if (newWidth === this.canvasWidth && newHeight === this.canvasHeight) {
      return;
    }
    this.canvasWidth = newWidth;
    this.canvasHeight = newHeight;
    // Re-apply stored viewOffset with updated dimensions so the principal point
    // shift stays correct after CSS-driven canvas resizes (e.g. keyboard close).
    // If an animation is in progress, retarget it instead of killing it.
    if (this.lastViewOffset) {
      if (this.isPrincipalPointAnimationActive) {
        this.retargetAnimation(this.lastViewOffset);
      } else {
        this.applyViewOffset(this.lastViewOffset, { animate: false });
      }
    }
  }

  applyViewOffset(
    viewOffset: CameraConfig['viewOffset'],
    options: { animate?: boolean; duration?: number } = {}
  ): void {
    this.lastViewOffset = viewOffset;
    const fullWidth = Math.max(1, this.canvasWidth);
    const fullHeight = Math.max(1, this.canvasHeight);
    const animateShift = Boolean(options.animate);
    const animationDuration = options.duration;

    let targetShift: PrincipalPointShift = { ...DEFAULT_SHIFT };

    if (viewOffset?.enabled) {
      const clampOffset = (value: number | undefined, max: number): number =>
        Math.min(Math.max(typeof value === 'number' ? value : 0, 0), max);

      const left = clampOffset(viewOffset.left, fullWidth);
      const right = clampOffset(viewOffset.right, fullWidth);
      const top = clampOffset(viewOffset.top, fullHeight);
      const bottom = clampOffset(viewOffset.bottom, fullHeight);

      const viewportWidth = Math.max(1, fullWidth - left - right);
      const viewportHeight = Math.max(1, fullHeight - top - bottom);
      const centerX = Math.min(Math.max(left + viewportWidth / 2, 0), fullWidth);
      const centerY = Math.min(Math.max(top + viewportHeight / 2, 0), fullHeight);

      targetShift = {
        px: -((centerX / fullWidth) * 2 - 1),
        py: (centerY / fullHeight) * 2 - 1,
      };
    }

    this.startPrincipalPointShiftAnimation(targetShift, animateShift, animationDuration);
  }

  forceSync(): void {
    this.camera.updateProjectionMatrix();
  }

  isAnimating(): boolean {
    return this.isPrincipalPointAnimationActive;
  }

  update(currentTime: number): void {
    if (!this.isPrincipalPointAnimationActive) {
      return;
    }
    const duration =
      this.principalPointAnimationDuration > 0
        ? this.principalPointAnimationDuration
        : PRINCIPAL_POINT_ANIMATION_DURATION;
    const elapsed = currentTime - this.principalPointAnimationStartTime;
    const progress = duration === 0 ? 1 : Math.min(1, elapsed / duration);
    const eased = cubicBezierEasing(progress);
    this.principalPointShift = {
      px: THREE.MathUtils.lerp(
        this.principalPointShiftStart.px,
        this.principalPointShiftTarget.px,
        eased
      ),
      py: THREE.MathUtils.lerp(
        this.principalPointShiftStart.py,
        this.principalPointShiftTarget.py,
        eased
      ),
    };
    if (progress >= 1) {
      this.principalPointShift = { ...this.principalPointShiftTarget };
      this.isPrincipalPointAnimationActive = false;
    }
    this.camera.updateProjectionMatrix();
  }

  private startPrincipalPointShiftAnimation(
    target: PrincipalPointShift,
    animate: boolean,
    animationDuration?: number
  ): void {
    const pxDelta = Math.abs(target.px - this.principalPointShift.px);
    const pyDelta = Math.abs(target.py - this.principalPointShift.py);
    if (!animate || (pxDelta < 0.0001 && pyDelta < 0.0001)) {
      this.setPrincipalPointShiftInstant(target);
      return;
    }
    this.principalPointShiftStart = { ...this.principalPointShift };
    this.principalPointShiftTarget = { ...target };
    this.principalPointAnimationStartTime = this.clock.getElapsedTime();
    this.principalPointAnimationDuration = typeof animationDuration === 'number'
      ? animationDuration
      : PRINCIPAL_POINT_ANIMATION_DURATION;
    this.isPrincipalPointAnimationActive = true;
  }

  /** Recalculate animation target for new canvas dimensions without restarting. */
  private retargetAnimation(viewOffset: CameraConfig['viewOffset']): void {
    this.lastViewOffset = viewOffset;
    const fullWidth = Math.max(1, this.canvasWidth);
    const fullHeight = Math.max(1, this.canvasHeight);
    let newTarget: PrincipalPointShift = { ...DEFAULT_SHIFT };
    if (viewOffset?.enabled) {
      const clamp = (v: number | undefined, max: number) =>
        Math.min(Math.max(typeof v === 'number' ? v : 0, 0), max);
      const left = clamp(viewOffset.left, fullWidth);
      const right = clamp(viewOffset.right, fullWidth);
      const top = clamp(viewOffset.top, fullHeight);
      const bottom = clamp(viewOffset.bottom, fullHeight);
      const vw = Math.max(1, fullWidth - left - right);
      const vh = Math.max(1, fullHeight - top - bottom);
      const cx = Math.min(Math.max(left + vw / 2, 0), fullWidth);
      const cy = Math.min(Math.max(top + vh / 2, 0), fullHeight);
      newTarget = {
        px: -((cx / fullWidth) * 2 - 1),
        py: (cy / fullHeight) * 2 - 1,
      };
    }
    this.principalPointShiftTarget = newTarget;
  }

  private setPrincipalPointShiftInstant(shift: PrincipalPointShift): void {
    this.principalPointShift = { ...shift };
    this.principalPointShiftTarget = { ...shift };
    this.isPrincipalPointAnimationActive = false;
    this.camera.updateProjectionMatrix();
  }

  private applyPrincipalPointShift(): void {
    const near = this.camera.near;
    const far = this.camera.far;
    const fovRad = THREE.MathUtils.degToRad(this.camera.fov);
    const aspect = this.camera.aspect;

    const top = near * Math.tan(fovRad / 2);
    const bottom = -top;
    const height = top - bottom;
    const width = height * aspect;
    const left = -width / 2;
    const right = width / 2;

    const xShift = (this.principalPointShift.px * (right - left)) / 2;
    const yShift = (this.principalPointShift.py * (top - bottom)) / 2;

    const l = left + xShift;
    const r = right + xShift;
    const b = bottom + yShift;
    const t = top + yShift;

    const x = (2 * near) / (r - l);
    const y = (2 * near) / (t - b);
    const a = (r + l) / (r - l);
    const b2 = (t + b) / (t - b);
    const c = -(far + near) / (far - near);
    const d = (-2 * far * near) / (far - near);

    this.principalPointMatrix.set(
      x, 0, a, 0,
      0, y, b2, 0,
      0, 0, c, d,
      0, 0, -1, 0
    );

    this.camera.projectionMatrix.copy(this.principalPointMatrix);
    this.camera.projectionMatrixInverse.copy(this.camera.projectionMatrix).invert();
  }
}
